import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { webUser } from 'services/apiDigitalTicket/users';

// Auth
import { getId } from 'helpers/auth';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import { operatorsApi } from 'services/apiDigitalTicket/operators';
import { setStorageWebUserData } from 'helpers/webUser';
import { getWebUserSuccess } from '../users/actions';

import * as TransportOperatorsActions from './actions';
import { TransportOperatorsTypes, TransportOperatorInterface } from './types';

// list({ payload }: ReturnType<typeof TransportOperatorsActions.listTransportOperatorsRequest>)
function* list({
  payload,
}: ReturnType<
  typeof TransportOperatorsActions.listTransportOperatorsRequest
>): any {
  try {
    yield put(
      DialogsActions.setSecondOpenLoading({ title: 'Carregando dados...' }),
    );
    const response = yield call(webUser.list, {
      params: {
        id: getId(),
        _includes: 'operators,operations',
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(
        TransportOperatorsActions.listTransportOperatorsSuccess({
          transportOperatorsList: response.data.TransportOperators,
        }),
      );
      yield put(
        getWebUserSuccess({
          webUserData: response.data,
        }),
      );
      yield put(DialogsActions.setSecondClose());
      yield put(DialogsActions.isLoading({ isLoading: false }));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportOperators - list - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DialogsActions.setSecondClose());
    yield put(TransportOperatorsActions.listTransportOperatorsFailure());
  }
}

function* selectOperator({
  payload,
}: ReturnType<
  typeof TransportOperatorsActions.selectTransportOperatorRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));
    yield put(
      TransportOperatorsActions.selectTransportOperatorSuccess({
        transportOperatorSelected: payload.transportOperatorSelected,
      }),
    );

    const response = yield call(webUser.list, {
      params: {
        id: getId(),
        _includes: 'operators,operations',
        transportOperatorId: payload.transportOperatorSelected.id,
      },
    });
    yield put(DialogsActions.setClose());

    setStorageWebUserData(JSON.stringify(response.data));
    if (payload.refresh) yield window.location.reload();
    if (payload.redirectTo) yield put(push(payload.redirectTo));

    // eslint-disable-next-line no-undef
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportOperators - select - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportOperatorsActions.listTransportOperatorsFailure());
  }
}

function* update({
  payload,
}: ReturnType<
  typeof TransportOperatorsActions.updateTransportOperatorRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando empresa' }));
    const response = yield call(operatorsApi.update, {
      data: { ...payload },
    });

    if (response.status === 200) {
      const operatorsList: TransportOperatorInterface[] = yield select(
        (state) => state.transportOperators.transportOperatorsList || [],
      );
      const operatorSelected: TransportOperatorInterface = yield select(
        (state) => state.transportOperators.transportOperatorSelected || {},
      );
      const updatedIndex = operatorsList.findIndex(
        (operator) => operator.id === response.data.id,
      );
      const selectedIndex = operatorsList.findIndex(
        (operator) => operator.id === operatorSelected.id,
      );
      operatorsList[updatedIndex] = {
        ...operatorsList[updatedIndex],
        name: response.data.name,
        cnpj: response.data.cnpj,
        photoUrl: response.data.photoUrl,
      };
      yield put(
        TransportOperatorsActions.updateTransportOperatorSuccess({
          transportOperatorsList: operatorsList,
          transportOperatorSelected: operatorsList[selectedIndex],
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Empresa alterada com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportOperators - update - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportOperatorsActions.updateTransportOperatorFailure());
    yield put(DialogsActions.setClose());
  }
}

function* requestAccess({
  payload,
}: ReturnType<typeof TransportOperatorsActions.operatorAccessRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Solicitando convite...' }),
    );
    const response = yield call(operatorsApi.requestAccess, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(TransportOperatorsActions.operatorAccessSuccess());

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pedido de acesso enviado com sucesso',
        }),
      );
    }
  } catch (error) {
    console.error('transportOperators - update - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Falha ao requisitar acesso',
      }),
    );

    yield put(TransportOperatorsActions.operatorAccessFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const transportOperatorsSagas = [
  takeLatest(TransportOperatorsTypes.LIST_TRANSPORT_OPERATORS_REQUEST, list),
  takeLatest(
    TransportOperatorsTypes.SELECT_TRANSPORT_OPERATOR_REQUEST,
    selectOperator,
  ),
  takeLatest(TransportOperatorsTypes.UPDATE_TRANSPORT_OPERATOR_REQUEST, update),
  takeLatest(TransportOperatorsTypes.OPERATOR_ACCESS_REQUEST, requestAccess),
];
